//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import {
  batchGetAllVariants,
  searchCuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApi';
import {
  BatchGetAllVariantsRequest,
  BatchGetAllVariantsResponse,
  CuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import ClientId from '@models/ClientId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';

export interface ProductsParams {
  catalogClientId?: ClientId;
  hasCatalog: boolean;
  limit: number;
  offset: number;
  search: string;
}

export const useProductsQuery = ({
  catalogClientId,
  hasCatalog,
  limit,
  offset,
  search,
}: ProductsParams) =>
  useQuery<CuralateCatalogProducts>({
    enabled: hasCatalog,
    queryFn: async ({ signal }) => {
      if (search.length > 0) {
        return searchCuralateCatalogProducts(
          { catalogClientId, limit, offset, search },
          { signal },
        );
      } else {
        return {
          products: [],
          totalCount: 0,
        };
      }
    },
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersProducts,
      {
        catalogClientId,
        limit,
        offset,
        search,
      },
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });

export function useProductDetailsQuery<T = BatchGetAllVariantsResponse>({
  fetchAllVariants,
  productStyleIds,
  select,
}: {
  fetchAllVariants?: boolean;
  productStyleIds: string[];
  select?: (data: BatchGetAllVariantsResponse) => T;
}) {
  return useQuery({
    queryFn: async ({ signal }) => {
      if (productStyleIds.length === 0) {
        return { productVariants: [] };
      }

      return batchGetAllVariants(
        { fetchAllVariants, productStyleIds },
        { signal },
      );
    },
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersProducts,
      QueryKeyConstants.MediaFiltersProductsDetails,
      {
        fetchAllVariants,
        productStyleIds,
      },
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}
